import { isArray, isEmpty, pick } from 'lodash-es';

const PREFER_STORE_KEY = {
  seven_eleven: 'seven_eleven_store',
  family_mart: 'family_mart_store',
};

const DEFAULT_STORE_PAYLOAD = {
  location_code: '',
  location_name: '',
  store_address: '',
};

export const STORE_CALLBACK_TYPES = {
  seven_eleven: 'callback',
  seven_eleven_cross_border: 'seven_eleven_cross_border_callback',
  family_mart: 'familymart_callback',
  family_mart_frozen: 'familymart_freeze_callback',
};

const DELIVERY_DATA_FIELDS = [
  'ref_data',
  'tracking_number',
  'shipment_number',
  'name_translations',
  'delivery_type',
  'url',
  'sn_id',
  'location_code',
  'location_name',
  'location_type',
  'store_address',
  'store_address_detail',
  'recipient_name',
  'recipient_phone',
  'consignee_id_type',
  'consignee_id_no',
  'recipient_phone_country_code',
  'hk_sfplus_home_region',
  'time_slot',
  'time_slot_key',
  'time_slot_group_key',
  'scheduled_delivery_date',
  'delivery_time_slot_id',
  // use for sf locker
  'code',
  'shop_name',
  'district_translations',
  // use for instore pickup
  'instore_pickup',
  'target_channel_id',
  // add keys for 7-11 cross border delivery
  'postal_code',
  'destination',
  'location_short_name',
];

const pickDeliveryData = (payload) => pick(payload, DELIVERY_DATA_FIELDS);

export const sortCartsByTags = (cartTags, carts) => {
  const cartTagIds = cartTags.map((cart) => cart._id);

  return cartTagIds.reduce((acc, key) => {
    if (!isEmpty(carts[key])) {
      acc[key] = carts[key];
    }
    return acc;
  }, {});
};

export const getSubmitAddress = (address, destination) => ({
  ...destination,
  delivery_address: address,
});

export const getSubmitMarketPickUp = (storeInfo, destination) => ({
  ...destination,
  delivery_data: pickDeliveryData(storeInfo),
});

export const getSubmitStorePickUp = (
  storePickupPayload,
  deliveryId,
  destination,
) => ({
  ...destination,
  delivery_data: pickDeliveryData(storePickupPayload[deliveryId]),
});

export const getSubmitSfLocker = (
  sfExpressPayload,
  sfExpressType,
  destination,
) => {
  const deliveryData = pickDeliveryData(sfExpressPayload[sfExpressType]);
  return {
    ...destination,
    delivery_data: { ...deliveryData, sfexpress_type: sfExpressType },
  };
};

export const getSubmitZeek = (zeekPayload, destination) => ({
  ...destination,
  delivery_data: pickDeliveryData(zeekPayload),
});

const getUserPreferredByKey = (order_preference = {}, key) => {
  const preferStore = order_preference[key];
  return !isEmpty(preferStore) ? preferStore : DEFAULT_STORE_PAYLOAD;
};

const getSevenFromSessionData = (callbackData) => {
  const {
    storeid: location_code,
    storename: location_name,
    storeaddress: store_address,
  } = callbackData;

  return {
    location_code,
    location_name,
    store_address,
  };
};

export const parseSevenData = (delivery, currentUser) => {
  const { order_preference } = currentUser;

  if (delivery.action === STORE_CALLBACK_TYPES.seven_eleven) {
    const callbackData = pick(delivery, [
      'storeid',
      'storename',
      'storeaddress',
    ]);
    return getSevenFromSessionData(callbackData);
  }

  return isEmpty(delivery.location_code)
    ? getUserPreferredByKey(order_preference, PREFER_STORE_KEY.seven_eleven)
    : delivery;
};

const getFamilyMartFromSessionData = (callbackData) => {
  const {
    cvsspot: location_code,
    name: location_name,
    addr: store_address,
  } = callbackData;

  return {
    location_code,
    location_name,
    store_address,
  };
};

export const parseFamilyData = (delivery, currentUser) => {
  const { order_preference } = currentUser;

  if (delivery.action === STORE_CALLBACK_TYPES.family_mart) {
    const callbackData = pick(delivery, ['cvsspot', 'name', 'addr']);
    return getFamilyMartFromSessionData(callbackData);
  }

  return isEmpty(delivery.location_code)
    ? getUserPreferredByKey(order_preference, PREFER_STORE_KEY.family_mart)
    : delivery;
};

const getFamilyMartFrozenFromSessionData = (callbackData) => {
  const {
    StoreId: location_code,
    Name: location_name,
    Addr: store_address,
    Items,
  } = callbackData;

  const reserved_items = isArray(Items) ? Items : [];
  const [firstItem = {}] = reserved_items;

  return {
    location_code,
    location_name,
    store_address,
    ref_data: {
      reserved_nos: reserved_items?.map((item) => item.ReservedNo),
      order_date: firstItem.OrderDate,
      ship_date: firstItem.ShipDate,
    },
  };
};

export const parseFamilyFrozenData = (delivery, currentUser) => {
  const { order_preference } = currentUser;

  if (delivery.action === STORE_CALLBACK_TYPES.family_mart_frozen) {
    const callbackData = pick(delivery, ['StoreId', 'Name', 'Addr', 'Items']);
    return getFamilyMartFrozenFromSessionData(callbackData);
  }

  return isEmpty(delivery.location_code)
    ? getUserPreferredByKey(order_preference, PREFER_STORE_KEY.family_mart)
    : delivery;
};

export const selectedDeliveriesFilter = (destinations, deliveryOptions) => {
  const selectedDeliveries = Object.entries(destinations).map(
    ([cartTagId, destination]) => {
      const deliveries = deliveryOptions[cartTagId] || [];
      const deliveryOption = deliveries.find(
        (delivery) => delivery._id === destination.delivery_option_id,
      );
      return { ...deliveryOption, cartTagId };
    },
  );
  return selectedDeliveries;
};

export const checkSkipDestinationWithOption = (deliveryOption) => {
  return (
    deliveryOption.region_type === 'custom' &&
    !deliveryOption.requires_customer_address
  );
};

export const checkSkipDestination = (deliveryOptions) =>
  deliveryOptions.every(checkSkipDestinationWithOption);
